class ShopTheLook {
  constructor(section) {
    this.section = section
    this.modal = section.querySelector('.ShopTheLookSection__Modal')
    this.modalOverlay = section.querySelector('.ShopTheLookSection__ModalOverlay')
    this.modalClose = section.querySelector('.ShopTheLookSection__ModalClose')
    this.openButtons = section.querySelectorAll('.ShopTheLookSection__OpenModal')
    this.productCards = section.querySelectorAll('.ShopTheLookSection__ProductCard')
    this.productsGrid = section.querySelector('.ShopTheLookSection__ProductsGrid')
    this.imageBlocks = section.querySelectorAll('.ShopTheLookSection__ImageBlock')
    this.mobileMediaQuery = window.matchMedia('(max-width: 768px)')
    this.modalOriginalParent = this.modal ? this.modal.parentNode : null
    this.closeTimer = null
    this.productsAmount = 0
    this.bodyScrollLocked = false

    if (this.modal) {
      this.init()
    }
  }

  init() {
    this.syncModalContainer()

    // Bind open modal buttons
    this.openButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.openModal(e))
    })

    // Bind close modal
    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.closeModal())
    }

    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', () => this.closeModal())
    }

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal()
      }
    })

    // Ensure modal is closed when navigating away
    window.addEventListener('pagehide', () => this.forceCloseModal())
    window.addEventListener('beforeunload', () => this.forceCloseModal())

    // Keep modal mounted to viewport context on mobile
    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', () => this.syncModalContainer())
    } else if (typeof this.mobileMediaQuery.addListener === 'function') {
      // Fallback for older browsers (e.g., older Safari)
      this.mobileMediaQuery.addListener(() => this.syncModalContainer())
    }
  }

  openModal(e) {
    const sourceBtn = e.currentTarget
    const productsFilter = sourceBtn.getAttribute('data-products')
    const isMobile = this.mobileMediaQuery.matches

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }

    // Show modal
    this.modal.classList.remove('closing')
    this.modal.classList.add('active')
    this.modal.setAttribute('aria-hidden', 'false')
    this.resetModalScroll()

    if (isMobile) {
      this.lockBodyScroll()
    }

    // Filter and display products
    this.productsAmount = this.filterProductsByImage(productsFilter)
    this.updateGridAlignment(this.productsAmount)

    // Scroll smoothly to the modal's section top
    const headerHeight = this.getHeaderHeight()
    const scrollY = this.section.getBoundingClientRect().top + window.pageYOffset - headerHeight

    // Only scroll if target is above current viewport
    if (!isMobile && scrollY < window.scrollY) {
      window.scrollTo({
        top: scrollY,
        behavior: 'smooth'
      })
    }

    this.modal.focus({ preventScroll: true })

    // Focus management (after scrolling)
    this.lastFocus = document.activeElement
  }

  closeModal() {
    this.modal.classList.add('closing')

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
    }

    this.closeTimer = window.setTimeout(() => {
      this.modal.classList.remove('active')
      this.modal.classList.remove('closing')
      this.modal.setAttribute('aria-hidden', 'true')
      this.unlockBodyScroll()
      this.closeTimer = null
    }, 300)

    // Restore focus
    if (this.lastFocus) {
      this.lastFocus.focus({ preventScroll: true })
    }
  }

  forceCloseModal() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }

    this.modal.classList.remove('active')
    this.modal.classList.remove('closing')
    this.modal.setAttribute('aria-hidden', 'true')
    this.unlockBodyScroll()
  }

  getHeaderHeight() {
    const root = document.documentElement
    const headerOffset = getComputedStyle(root).getPropertyValue('--header-height').trim()
    const headerHeight = parseFloat(headerOffset)
    return Number.isNaN(headerHeight) ? 0 : headerHeight
  }

  resetModalScroll() {
    if (!this.productsGrid) return
    this.productsGrid.scrollTop = 0
  }

  filterProductsByImage(filterValue) {
    if (!this.productsGrid) return 0

    let visibleCount = 0

    // Filter products by their data-products attribute
    this.productCards.forEach((card) => {
      const cardFilter = card.getAttribute('data-products')
      if (cardFilter === filterValue) {
        card.style.display = 'block'
        visibleCount++
      } else {
        card.style.display = 'none'
      }
    })

    return visibleCount
  }

  updateGridAlignment(visibleCount) {
    if (!this.productsGrid) return

    const shouldAlign = visibleCount <= 2
    this.productsGrid.classList.toggle('ShopTheLookSection__ProductsGrid--aligned', shouldAlign)

    if (!shouldAlign) {
      this.productsGrid.style.removeProperty('--shop-the-look-grid-offset-top')
      return
    }

    if (!this.imageBlocks?.[0]) return

    const padding =
       this.imageBlocks[0].getBoundingClientRect().top - this.section.getBoundingClientRect().top - this.modalClose.getBoundingClientRect().height

    this.productsGrid.style.setProperty('--shop-the-look-grid-offset-top', `${padding}px`)
  }

  syncModalContainer() {
    if (!this.modal || !this.modalOriginalParent) return

    const isModalActive = this.modal.classList.contains('active')

    if (this.mobileMediaQuery.matches) {
      if (this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal)
      }

      if (isModalActive) {
        this.lockBodyScroll()
      } else {
        this.unlockBodyScroll()
      }

      return
    }

    if (this.modal.parentNode !== this.modalOriginalParent) {
      this.modalOriginalParent.appendChild(this.modal)
      this.updateGridAlignment(this.productsAmount)
    }

    this.unlockBodyScroll()
  }

  lockBodyScroll() {
    if (this.bodyScrollLocked) return

    document.body.style.overflow = 'hidden'
    this.bodyScrollLocked = true
  }

  unlockBodyScroll() {
    if (!this.bodyScrollLocked) return

    document.body.style.overflow = ''
    this.bodyScrollLocked = false
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function () {
  const sections = document.querySelectorAll('[data-section-type="shop-the-look"]')
  sections.forEach((section) => {
    new ShopTheLook(section)
  })
})

// Handle Shopify theme editor integration
if (window.Shopify && window.Shopify.theme) {
  window.addEventListener('shopify:section:load', function (e) {
    const section = e.detail.target
    if (section.getAttribute('data-section-type') === 'shop-the-look') {
      new ShopTheLook(section)
    }
  })
}
